import { useMemo } from "react";
import { useAuthStore } from "@/hooks/useAuthStore";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { PERMISSIONS, ROLES, type AppRole, type Permission } from "@/constants/permissions";
import {
  assignableRolesFor,
  canManageMember as canManageMemberForTier,
  isSubcontractorOnlyIn,
} from "@/lib/orgRoles";
import type { ProjectAssignment } from "@/stores/permissions/repository";
import { useProjectStatusEnabled } from "@/domains/projects/status/projectStatusAvailability";
import {
  isConfigurablePermission,
  projectScopedAnswer,
  type ProjectPermissionSet,
} from "@/lib/memberProjectPermissions";
import {
  hasLegacyBillingAccess,
  useBillingStatus,
  type BillingStatusContextValue,
} from "@/domains/billing/BillingStatusContext";

/**
 * Why billing is withholding mutations right now, or null.
 *
 * A machine code, not prose: usePermissions is called from hundreds of places
 * including hooks with no language provider, so whoever keeps a withheld control
 * on screen translates the code itself (`billingBlockMessage`).
 *
 * Read only from `billingBlockFor`. The restriction alone is not exposed,
 * because the two codes do not withhold the same thing: `read_only` is the
 * server's settled verdict and takes the whole mutation set, `status_unknown`
 * only means we could not read the context and takes one permission.
 */
export type BillingRestriction = "read_only" | "status_unknown" | "activation_pending";

/**
 * Why one control is unavailable for a billing reason, or null.
 *
 * Distinct from `BillingRestriction`: this answers "would this user hold this
 * permission but for billing?", which is what a control needs in order to stay
 * on screen and explain itself instead of vanishing. A user who never held the
 * permission gets null — a disabled button they could never use is noise.
 */
export type BillingBlock =
  | BillingRestriction
  | "details_missing"
  | "payment_method_missing"
  | "plan_selection_required";

export interface PermissionContext {
  buildingId?: number;
  projectId?: number;
}

function configuredProjectAnswer(
  permission: Permission,
  projectId: number | undefined,
  projectPermissions: ReadonlyMap<number, ProjectPermissionSet> | null
): boolean | undefined {
  if (!projectId || !projectPermissions) return undefined;
  const answer = projectScopedAnswer(permission, projectId, projectPermissions);
  if (answer === false) return false;
  if (answer === true && isConfigurablePermission(permission)) return true;
  return undefined;
}

type BuildingBillingScope = Pick<
  BillingStatusContextValue,
  "buildingSubscription" | "buildingProjectIds"
>;

function projectBuildingId(
  projectId: number,
  assignments: ReadonlyMap<number, ProjectAssignment>,
  billing: BuildingBillingScope
): number | undefined {
  const assignedBuilding = assignments.get(projectId)?.buildingId;
  if (assignedBuilding !== undefined) return assignedBuilding;
  if (billing.buildingProjectIds?.has(projectId)) return billing.buildingSubscription?.building_id;
  return undefined;
}

function buildingIdForContext(
  context: PermissionContext | undefined,
  assignments: ReadonlyMap<number, ProjectAssignment>,
  billing: BuildingBillingScope
) {
  if (context?.buildingId !== undefined) return context.buildingId;
  if (context?.projectId) return projectBuildingId(context.projectId, assignments, billing);
  return billing.buildingSubscription?.building_id;
}

// Mutations that CREATE or ENLARGE what the organisation is charged for, or
// that write into the content it stopped paying for. The brief names adding
// buildings, opening projects and uploading documents, then generalises to
// "every mutating action"; this is that generalisation, with one rule drawn
// through it.
//
// ACCESS CONTROL IS NOT A BILLING LEVER. Who may reach this organisation's data
// stays the organisation's to decide while it is read-only, so member roles,
// org invites and project permissions are NOT withheld. An unpaid invoice must
// never be the reason a departing employee keeps their access — that trades a
// billing problem for a security one, and nothing server-side supports it
// either: the enforcement migration guards `buildings`, `building_projects`,
// `documents` and the documents bucket, and stops there. Blocking it in the UI
// alone would be theatre with a real cost.
//
// MANAGE_ORG_PERMISSIONS is likewise absent, for a different reason: it is the
// VIEW gate for Organization Settings (routes, sidebar, isAdmin), so blocking it
// would bounce an org admin off /organization-settings — permanently in a
// read-only org, and briefly on every cold load while the billing RPC is still
// in flight.
//
// ALLOW_SIGNING stays in. It gates STARTING a signing (`useSigningAccess`),
// which is a new commitment; it does not reach a chain already in flight —
// `useSigningVisible` never reads it, `dokobit-sign-open` deliberately does not
// re-check it (design §4.5), and the sweep keeps mailing turn letters — so no
// counterparty is stranded and the sender keeps their Cancel button.
const BILLING_BLOCKED_MUTATIONS: ReadonlySet<Permission> = new Set([
  PERMISSIONS.ADD_DATA,
  PERMISSIONS.MODIFY_DATA,
  PERMISSIONS.DELETE_DATA,
  PERMISSIONS.ADD_PROJECT_FILES,
  PERMISSIONS.DELETE_PROJECT_FILES,
  PERMISSIONS.UPLOAD_DOCUMENTS,
  PERMISSIONS.PROJECT_WRITE_FILES,
  PERMISSIONS.PROJECT_WRITE_FOLDERS,
  PERMISSIONS.PROJECT_SOFT_DELETE,
  PERMISSIONS.PROJECT_GENERATE_SHARE_LINK,
  PERMISSIONS.MODIFY_PROJECT_FILES,
  PERMISSIONS.MANAGE_BUILDING_DATA_SIDEBAR,
  PERMISSIONS.CREATE_BUILDINGS,
  PERMISSIONS.CREATE_PROJECTS,
  PERMISSIONS.MODIFY_PROJECTS,
  PERMISSIONS.MANAGE_PM,
  PERMISSIONS.MANAGE_DRAWING_COMPARE,
  PERMISSIONS.ALLOW_SIGNING,
]);

// What an UNREADABLE billing status withholds — deliberately not the set above.
// "Read-only" is the server's verdict; "unknown" is only our failure to fetch a
// context, and EVERY failure produces it: an RPC missing from an environment,
// an RLS denial, a sustained network fault. Withholding uploads, PM, signing and
// every project write org-wide on that would make the deploy order load-bearing
// and cost far more than it protects. Creating a building is the one action
// billing meters, the one the edge function refuses server-side regardless, and
// the only one that can add money to an invoice we cannot currently read — so it
// is the only one withheld while the status is unknown.
const BILLING_UNKNOWN_BLOCKED_MUTATIONS: ReadonlySet<Permission> = new Set([
  PERMISSIONS.CREATE_BUILDINGS,
]);

function assignedContactAccess(
  projectId: number | null | undefined,
  assignedProjects: ReadonlyMap<number, ProjectAssignment>,
  viewsIn: (assignment: ProjectAssignment) => boolean
): boolean {
  if (projectId != null) {
    const assignment = assignedProjects.get(projectId);
    return !!assignment && viewsIn(assignment);
  }
  return Array.from(assignedProjects.values()).some(viewsIn);
}

export function usePermissions() {
  const auth = useAuthStore();
  const { currentOrganization, isReady } = useOrganizationContext();
  const billing = useBillingStatus();
  const orgId = currentOrganization?.id;
  // Read observable values so MobX can track and trigger re-renders
  const permSet = orgId ? auth.getPermissions(orgId) : undefined;
  const assignedBuildingsRef = auth.assignedBuildings;
  const assignedProjectsRef = auth.assignedProjects;
  // Status controls stay hidden until the database is known to have the column.
  const projectStatusEnabled = useProjectStatusEnabled();
  const projectPermissionsRef = auth.projectPermissions;
  const isContactsAccessReady = Boolean(isReady && orgId && auth.isRbacLoadedFor(orgId));

  return useMemo(() => {
    if (!currentOrganization || !auth.userId) {
      const no = () => false;
      return {
        hasPermission: no,
        holdsPermission: no,
        permissions: new Set<Permission>(),
        assignedBuildings: new Set<number>(),
        assignedProjects: new Map<number, ProjectAssignment>(),
        canViewAllBuildings: false,
        canViewAssignedBuildings: false,
        canAddData: false,
        canModifyData: false,
        canDeleteData: false,
        canManageOrgPermissions: false,
        canManageProjectPermissions: false,
        canUploadDocuments: false,
        currentRole: "anonymous",
        isPlatformAdmin: false,
        isAdmin: false,
        isEditor: false,
        isViewer: false,
        isSubcontractorOnly: false,
        isOrgOwner: false,
        isOrgAdmin: false,
        canManageMembers: false,
        canManageMember: no,
        assignableMemberRoles: [] as AppRole[],
        canArchiveBuildings: false,
        canRestoreBuildings: false,
        canDeleteBuildings: false,
        canEditAllBuildings: false,
        canInviteUsers: false,
        canManageOrganization: false,
        canPermanentlyDeleteDocuments: false,
        canReadProject: no,
        canWriteFilesInProject: no,
        canWriteFoldersInProject: no,
        canModifyFilesInProject: no,
        canSoftDeleteInProject: no,
        canGenerateShareLinkInProject: no,
        canViewChatbot: false,
        canUseChatbot: false,
        canViewPMTool: false,
        canViewDrawingCompareTool: false,
        canViewBuildingDataSidebar: no,
        canManageBuildingDataSidebar: no,
        canCreateBuildings: false,
        canChooseBuildingPlan: false,
        canCreateProjects: false,
        canModifyProjects: false,
        canManageProjectStatus: false,
        canArchiveProjects: false,
        canViewLogOnProject: no,
        canManagePMOnProject: no,
        billingBlockFor: () => null as BillingBlock | null,
        canViewPMOnProject: no,
        canViewDrawingCompareOnProject: no,
        canViewContactsOnProject: no,
        canUseModelViewerOnProject: no,
        canViewBriefs: false,
        canViewBriefsOnProject: no,
        canManageBriefsOnProject: no,
        canViewContacts: no,
        canManageContactsOnProject: no,
        isContactsAccessReady,
        isRbacReady: isContactsAccessReady,
      } as const;
    }

    const orgId = currentOrganization.id;
    const userPermissions = permSet ?? new Set<Permission>();
    const assignedBuildings = assignedBuildingsRef;
    const assignedProjects = assignedProjectsRef as ReadonlyMap<number, ProjectAssignment>;
    const projectPermissions = projectPermissionsRef;
    // Roles come solely from org_user_roles (unified model). hasRoleInOrg is
    // multi-role-safe. org_admin has full parity with org_owner here — the only
    // difference lives in the member-management tier rules (canManageMember).
    const isOrgOwner = auth.hasRoleInOrg(orgId, ROLES.ORG_OWNER);
    const isOrgAdmin = auth.hasRoleInOrg(orgId, ROLES.ORG_ADMIN);

    // A money gate that opens on a transient PostgREST error is not a gate, so an
    // unreadable status still fails closed — over one permission, not twenty.
    const billingRestriction: BillingRestriction | null = billing.isReadOnly
      ? "read_only"
      : billing.mutationStatusUnknown
        ? "status_unknown"
        : null;
    const billingReadOnly = billingRestriction === "read_only";

    const PROJECT_SCOPED: ReadonlySet<Permission> = new Set<Permission>([
      PERMISSIONS.VIEW_ASSIGNED_PROJECTS,
      PERMISSIONS.ADD_PROJECT_FILES,
      PERMISSIONS.DELETE_PROJECT_FILES,
      PERMISSIONS.MODIFY_PROJECT_FILES,
      PERMISSIONS.MANAGE_PROJECT_PERMISSIONS,
      PERMISSIONS.PROJECT_READ,
      PERMISSIONS.PROJECT_WRITE_FILES,
      PERMISSIONS.PROJECT_WRITE_FOLDERS,
      PERMISSIONS.PROJECT_SOFT_DELETE,
      PERMISSIONS.PROJECT_GENERATE_SHARE_LINK,
      PERMISSIONS.PROJECT_VIEW_LOG,
      PERMISSIONS.BUILDING_VIEW_PROJECTS,
      PERMISSIONS.VIEW_BRIEFS,
      PERMISSIONS.MANAGE_BRIEFS,
      PERMISSIONS.VIEW_CONTACTS,
      PERMISSIONS.MANAGE_CONTACTS,
      PERMISSIONS.USE_MODEL_VIEWER,
    ]);

    // The role grant alone, with no billing gate. Split out so a withheld
    // control can tell "you were never allowed this" (hide it) from "billing is
    // withholding it right now" (keep it, and say why).
    const assignmentAllows = (permission: Permission, context?: PermissionContext): boolean => {
      if (context?.buildingId && permission === PERMISSIONS.VIEW_ASSIGNED_BUILDINGS)
        return assignedBuildings.has(context.buildingId);
      if (context?.projectId && PROJECT_SCOPED.has(permission))
        return assignedProjects.has(context.projectId);
      return true;
    };

    const holdsPermission = (permission: Permission, context?: PermissionContext): boolean => {
      // Owner/admin bypass: UI safety net that mirrors the DB-level
      // has_permission() short-circuit (extended to org_admin). The DB remains
      // the source of truth; this just prevents the UI from hiding buttons/tabs
      // the owner/admin is authorized to use.
      if (isOrgOwner || isOrgAdmin) return true;
      // Per-project permissions (overrides included) answer the configurable
      // codes outright — an override can grant a code the role does not — and
      // narrow the UI flags that stand for them (see PROJECT_FLAG_REQUIRES).
      // `projectPermissions` is null only when the database predates them.
      const answer = configuredProjectAnswer(permission, context?.projectId, projectPermissions);
      if (answer !== undefined) return answer;
      // A flag whose code is on still needs the role and assignment to grant it.
      return userPermissions.has(permission) && assignmentAllows(permission, context);
    };

    /**
     * Why billing is withholding a permission the user otherwise holds, or null.
     *
     * Details-missing and payment-method-missing are both scoped to
     * CREATE_BUILDINGS because that is the only thing the server refuses for
     * them; a pilot organisation is exempt from both, having no billing details
     * to give and no subscription to charge. They carry the same standing —
     * `private.org_billing_state` orders payment_method_missing straight after
     * details_missing and `org_billing_can_add_building` refuses either — so
     * details is reported first only because it is the earlier repair.
     */
    // What the server refuses a bill-increasing action for, in the same order
    // `private.org_billing_state` reports it. A pilot organisation is exempt
    // from both: it has no billing details to give and no subscription to charge.
    const billIncreaseBlock = (): BillingBlock | null => {
      if (!isOrgOwner && billing.context?.allows_legacy_building_creation === false)
        return "plan_selection_required";
      if (billing.isPilot) return null;
      if (billing.detailsMissing) return "details_missing";
      if (billing.paymentMethodMissing) return "payment_method_missing";
      return null;
    };

    const buildingContentBlock = (
      permission: Permission,
      context?: PermissionContext
    ): BillingBlock | null | undefined => {
      if (permission === PERMISSIONS.CREATE_BUILDINGS || !BILLING_BLOCKED_MUTATIONS.has(permission))
        return undefined;
      const subscription = billing.buildingSubscription;
      const buildingId = buildingIdForContext(context, assignedProjects, {
        buildingSubscription: billing.buildingSubscription,
        buildingProjectIds: billing.buildingProjectIds,
      });
      const scopedBuilding = subscription?.building_id === buildingId ? subscription : undefined;
      if (scopedBuilding?.activation_pending) return "activation_pending";
      if (scopedBuilding?.billing_exempt === true) return null;
      return undefined;
    };

    const billingBlockFor = (
      permission: Permission,
      context?: PermissionContext
    ): BillingBlock | null => {
      if (!holdsPermission(permission, context)) return null;
      if (hasLegacyBillingAccess(billing)) return null;
      const contentBlock = buildingContentBlock(permission, context);
      if (contentBlock !== undefined) return contentBlock;
      if (billingRestriction) {
        const withheld = billingReadOnly
          ? BILLING_BLOCKED_MUTATIONS
          : BILLING_UNKNOWN_BLOCKED_MUTATIONS;
        if (withheld.has(permission)) return billingRestriction;
      }
      return permission === PERMISSIONS.CREATE_BUILDINGS ? billIncreaseBlock() : null;
    };

    const hasPermission = (permission: Permission, context?: PermissionContext): boolean =>
      holdsPermission(permission, context) && billingBlockFor(permission, context) === null;

    const canViewAllBuildings = hasPermission(PERMISSIONS.VIEW_ALL_ORG_BUILDINGS);
    const canViewAssignedBuildings = hasPermission(PERMISSIONS.VIEW_ASSIGNED_BUILDINGS);
    const canAddData = hasPermission(PERMISSIONS.ADD_DATA);
    const canModifyData = hasPermission(PERMISSIONS.MODIFY_DATA);
    const canDeleteData = hasPermission(PERMISSIONS.DELETE_DATA);
    const canManageOrgPermissions = hasPermission(PERMISSIONS.MANAGE_ORG_PERMISSIONS);
    const canManageProjectPermissions = hasPermission(PERMISSIONS.MANAGE_PROJECT_PERMISSIONS);
    const canUploadDocuments = hasPermission(PERMISSIONS.UPLOAD_DOCUMENTS);

    const isAdmin = canManageOrgPermissions;
    const isEditor = canModifyData || canAddData;
    const isViewer =
      canViewAllBuildings ||
      canViewAssignedBuildings ||
      hasPermission(PERMISSIONS.VIEW_ASSIGNED_PROJECTS);
    const currentRole: string = isAdmin
      ? "admin"
      : isEditor
        ? "editor"
        : isViewer
          ? "viewer"
          : "anonymous";

    // Project-scoped computed booleans. These take a projectId (the user may hold
    // the permission via a project assignment for one project but not another).
    const canReadProject = (projectId: number) =>
      hasPermission(PERMISSIONS.PROJECT_READ, { projectId });
    const canWriteFilesInProject = (projectId: number) =>
      hasPermission(PERMISSIONS.PROJECT_WRITE_FILES, { projectId });
    const canWriteFoldersInProject = (projectId: number) =>
      hasPermission(PERMISSIONS.PROJECT_WRITE_FOLDERS, { projectId });
    const canModifyFilesInProject = (projectId: number) =>
      hasPermission(PERMISSIONS.MODIFY_PROJECT_FILES, { projectId });
    const canSoftDeleteInProject = (projectId: number) =>
      hasPermission(PERMISSIONS.PROJECT_SOFT_DELETE, { projectId });
    const canGenerateShareLinkInProject = (projectId: number) =>
      hasPermission(PERMISSIONS.PROJECT_GENERATE_SHARE_LINK, { projectId });
    const canViewChatbot = hasPermission(PERMISSIONS.VIEW_CHATBOT);
    const canUseChatbot = hasPermission(PERMISSIONS.USE_CHATBOT);
    const canViewLogOnProject = (projectId: number) =>
      hasPermission(PERMISSIONS.PROJECT_VIEW_LOG, { projectId });

    // May I change PM data in this project — including defining the project's
    // own issue statuses and linking issues into a schedule? Project-scoped
    // because MANAGE_PM is `requires_context`; this mirrors the pm_* RLS
    // policies exactly, so it is an affordance, never the boundary.
    const canManagePMOnProject = (projectId: number) =>
      hasPermission(PERMISSIONS.MANAGE_PM, { projectId });

    // May I open this project's IFC models in the model viewer?
    // USE_MODEL_VIEWER arrives with a franklin-data-pipeline #318 migration that
    // grants it to every org role. Until that applies no role holds the code at
    // all, and members must keep the model viewer they have always had — so a
    // role that does not grant it at all reads as "not migrated yet", allowed.
    // Once granted, the per-project effective set decides (overrides included).
    const canUseModelViewerOnProject = (projectId: number) =>
      isOrgOwner ||
      isOrgAdmin ||
      !userPermissions.has(PERMISSIONS.USE_MODEL_VIEWER) ||
      hasPermission(PERMISSIONS.USE_MODEL_VIEWER, { projectId });

    // May I see this project's issues / use its drawing viewer? Both codes are
    // member-configurable per project, so these answer from the per-project
    // effective set (the role path before the schema has it). They drive the
    // dimmed sidebar items and ProjectPermissionRouteGuard; RLS is the boundary.
    const canViewPMOnProject = (projectId: number) =>
      hasPermission(PERMISSIONS.VIEW_PM, { projectId });
    const canViewDrawingCompareOnProject = (projectId: number) =>
      hasPermission(PERMISSIONS.VIEW_DRAWING_COMPARE, { projectId });

    // Project brief. The module-level boolean (no context) gates the sidebar
    // tab and the route, as VIEW_PM_TOOL does for the PM pages; the
    // project-scoped pair mirrors the project_brief* RLS policies and the
    // brief_* RPCs' has_permission checks, so they are affordances, never
    // the boundary.
    const canViewBriefs = hasPermission(PERMISSIONS.VIEW_BRIEFS);
    const canViewBriefsOnProject = (projectId: number) =>
      hasPermission(PERMISSIONS.VIEW_BRIEFS, { projectId });
    const canManageBriefsOnProject = (projectId: number) =>
      hasPermission(PERMISSIONS.MANAGE_BRIEFS, { projectId });

    // PM tool is governed by the VIEW_PM_TOOL role grant (WHA-3301 grants it to
    // org_owner + subcontractor). Uses the standard hasPermission path — same
    // semantics as the /issues route's PermissionGuard (org_owner bypass + grant)
    // so the sidebar and route never disagree. Per-issue access is enforced by
    // the project-scoped VIEW_PM / MANAGE_PM RLS policies. No platform-admin bypass.
    const canViewPMTool = hasPermission(PERMISSIONS.VIEW_PM_TOOL);

    // Drawing Compare, same shape as the PM tool. Granted to all three org
    // roles in franklin-data-pipeline migration 20260803105531, replacing the
    // temporary isPlatformAdmin gate the tool shipped with. Per-project access
    // is enforced server-side by request_drawing_geometry's VIEW_DRAWING_COMPARE
    // check and by the drawing_comparison* RLS policies, so this stays a plain
    // module gate with no context. Deliberately NO platform-admin bypass: the
    // /tools hub is still staff-only, but the tool it hosts is a customer
    // feature and must not be gated on Franklin-staff identity.
    const canViewDrawingCompareTool = hasPermission(PERMISSIONS.VIEW_DRAWING_COMPARE_TOOL);

    // Contacts is project-scoped: a selected project needs its own assignment,
    // while building scope needs at least one assignment in that building.
    // Check the organization too because assignments are cached across orgs.
    const canViewContacts = ({
      buildingId,
      projectId,
      buildingOrgId,
    }: {
      buildingId: number;
      projectId?: number | null;
      buildingOrgId?: string;
    }) => {
      if (!hasPermission(PERMISSIONS.VIEW_CONTACTS)) return false;
      if (buildingOrgId && buildingOrgId !== orgId) return false;
      if (isOrgOwner || isOrgAdmin) return buildingOrgId === orgId;
      // Per project, VIEW_CONTACTS is member-configurable: an assignment alone
      // is not enough once an admin has switched contacts off for it.
      const viewsIn = (assignment: ProjectAssignment) =>
        assignment.orgId === orgId &&
        assignment.buildingId === buildingId &&
        hasPermission(PERMISSIONS.VIEW_CONTACTS, { projectId: assignment.projectId });
      return assignedContactAccess(projectId, assignedProjects, viewsIn);
    };
    // One project's contacts, for the dimmed sidebar item and the route guard
    // (canViewContacts answers for a building or a project and decides whether
    // the tab exists at all).
    const canViewContactsOnProject = (projectId: number) =>
      isOrgOwner ||
      isOrgAdmin ||
      (assignedProjects.get(projectId)?.orgId === orgId &&
        hasPermission(PERMISSIONS.VIEW_CONTACTS, { projectId }));
    const canManageContactsOnProject = (projectId: number) =>
      isOrgOwner ||
      isOrgAdmin ||
      (assignedProjects.get(projectId)?.orgId === orgId &&
        hasPermission(PERMISSIONS.MANAGE_CONTACTS, { projectId }));

    // Building data sidebar (WHA-3488). Building-scoped: VIEW gates seeing the
    // right-side data panel, MANAGE gates editing attribute values in it.
    // Platform admins always have access; otherwise the standard hasPermission
    // path applies (org_owner bypass + role grant).
    const canViewBuildingDataSidebar = (buildingId: number) =>
      auth.isPlatformAdmin || hasPermission(PERMISSIONS.VIEW_BUILDING_DATA_SIDEBAR, { buildingId });
    const canManageBuildingDataSidebar = (buildingId: number) =>
      auth.isPlatformAdmin ||
      hasPermission(PERMISSIONS.MANAGE_BUILDING_DATA_SIDEBAR, { buildingId });

    // Org-shell create actions and project metadata edits. These ride the
    // standard hasPermission path (org_owner bypass kicks in), so granting
    // CREATE_BUILDINGS / CREATE_PROJECTS / MODIFY_PROJECTS to a role later
    // exposes the buttons without a code change. Subcontractors don't hold
    // these so the affected UI is hidden for them.
    // "Details missing blocks adding buildings" is enforced server-side too;
    // this only keeps the button from offering an action that would be refused.
    // Pilot organisations are exempt — they have no billing details to give.
    // billingBlockFor carries both that rule and the read-only one.
    const canCreateBuildings = hasPermission(PERMISSIONS.CREATE_BUILDINGS);
    const canCreateProjects = hasPermission(PERMISSIONS.CREATE_PROJECTS);
    const canModifyProjects = hasPermission(PERMISSIONS.MODIFY_PROJECTS);
    const canManageProjectStatus =
      projectStatusEnabled && hasPermission(PERMISSIONS.MANAGE_PROJECT_STATUS);

    // Is the user a subcontractor without any other role in this org?
    // Drives UI-shell restrictions (hide the dashboards; Projects view only —
    // see useIsProjectScopedMember).
    // Roles are sourced solely from org_user_roles under the unified model.
    const isSubcontractorOnly = isSubcontractorOnlyIn(
      (org, role) => auth.hasRoleInOrg(org, role),
      orgId
    );

    return {
      hasPermission,
      // The role grant with NO billing gate. Read it for an action that lowers
      // the bill (archiving, removing a building) — the server allows those
      // while read-only, so gating them on the billing-aware boolean would trap
      // an organisation in the state it is trying to leave.
      holdsPermission,
      permissions: userPermissions,
      assignedBuildings,
      assignedProjects,
      canViewAllBuildings,
      canViewAssignedBuildings,
      canAddData,
      canModifyData,
      canDeleteData,
      canManageOrgPermissions,
      canManageProjectPermissions,
      canUploadDocuments,
      currentRole,
      isPlatformAdmin: auth.isPlatformAdmin,
      isAdmin,
      isEditor,
      isViewer,
      isSubcontractorOnly,
      isOrgOwner,
      isOrgAdmin,
      // Org-scoped member management (Organization Settings → Users). This is
      // NOT platform admin — see src/lib/orgRoles.ts for the tier matrix. It is
      // deliberately NOT withheld while read-only: see the rule above
      // BILLING_BLOCKED_MUTATIONS — an unpaid invoice must not be the reason an
      // organisation cannot revoke a departing member's access.
      canManageMembers: isOrgOwner || isOrgAdmin,
      canManageMember: (targetRole: string) =>
        canManageMemberForTier({ isOwner: isOrgOwner, isAdmin: isOrgAdmin }, targetRole),
      assignableMemberRoles: assignableRolesFor({ isOwner: isOrgOwner, isAdmin: isOrgAdmin }),
      // ARCHIVING IS NOT WITHHELD WHILE READ-ONLY, and that is deliberate.
      // Archiving is the one lever that LOWERS the bill, so refusing it would
      // punish the organisation for the state it is trying to leave — the same
      // rule the server keeps: `refuseAction` gates only `add_building` /
      // `restore_building` (the bill-increasing pair), and the write guard is
      // INSERT-only, so an archive flip is never refused. Withholding it here
      // would be a UI-only block on an action the backend deliberately allows.
      //
      // Billing adds a second removal path on /billing; it does not retire this
      // one, which is still the only way back for a building archived before
      // billing existed.
      canArchiveBuildings: holdsPermission(PERMISSIONS.MODIFY_DATA),
      // Un-archiving is the mirror image: it re-opens a service row and raises
      // the subscription quantity, so it follows `restore_building` and stays
      // withheld while read-only.
      canRestoreBuildings: canModifyData,
      canDeleteBuildings: canDeleteData,
      canEditAllBuildings: canModifyData || canAddData,
      canInviteUsers: hasPermission(PERMISSIONS.MANAGE_ORG_INVITES),
      canManageOrganization: canManageOrgPermissions,
      canPermanentlyDeleteDocuments: canDeleteData,
      canReadProject,
      canWriteFilesInProject,
      canWriteFoldersInProject,
      canModifyFilesInProject,
      canSoftDeleteInProject,
      canGenerateShareLinkInProject,
      canViewChatbot,
      canUseChatbot,
      canViewPMTool,
      canViewDrawingCompareTool,
      canViewBuildingDataSidebar,
      canManageBuildingDataSidebar,
      canCreateBuildings,
      // Opens a priced plan choice, including Free. The billing service checks
      // capacity and payment before creating the selected subscription.
      canChooseBuildingPlan: isOrgOwner && holdsPermission(PERMISSIONS.CREATE_BUILDINGS),
      canCreateProjects,
      canModifyProjects,
      canManageProjectStatus,
      // Project archive/restore mirrors building archive: gated on MODIFY_DATA
      // (org owners/admins), same as canArchiveBuildings.
      canArchiveProjects: canModifyData,
      canViewLogOnProject,
      canManagePMOnProject,
      // Why a control the user DOES hold is withheld right now. A control that
      // reads this stays on screen and explains itself instead of disappearing
      // with no account of where it went.
      billingBlockFor,
      canViewPMOnProject,
      canViewDrawingCompareOnProject,
      canViewContactsOnProject,
      canUseModelViewerOnProject,
      canViewBriefs,
      canViewBriefsOnProject,
      canManageBriefsOnProject,
      canViewContacts,
      canManageContactsOnProject,
      isContactsAccessReady,
      // Every grant above reads false until this org's permissions AND
      // assignments have loaded; a "may not" shown before then is premature.
      isRbacReady: isContactsAccessReady,
    } as const;
  }, [
    auth,
    currentOrganization,
    permSet,
    assignedBuildingsRef,
    assignedProjectsRef,
    billing.isReadOnly,
    billing.detailsMissing,
    billing.paymentMethodMissing,
    billing.isPilot,
    billing.mutationStatusUnknown,
    billing.buildingSubscription,
    billing.buildingProjectIds,
    billing.context?.allows_legacy_building_creation,
    billing.context?.org_id,
    billing.orgId,
    projectPermissionsRef,
    isContactsAccessReady,
    projectStatusEnabled,
  ]);
}
